import React, { useMemo } from 'react';

interface MiniHourlyChartProps {
  hourly: {
    hoje: number[];
    ontem: number[];
    semana_anterior: number[];
  };
  width: number;
  height: number;
  showLegend?: boolean;
}

/**
 * Mini SVG chart for displaying hourly comparison data
 * Renders 3 series:
 * - Today: Blue area fill + line
 * - Yesterday: Purple line
 * - Last week: Green dashed line
 */
const MiniHourlyChart: React.FC<MiniHourlyChartProps> = ({
  hourly,
  width,
  height,
  showLegend = false
}) => {
  const padding = 2;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const { areaPath, todayPath, ontemPath, semanaPath, max } = useMemo(() => {
    // Find max value for normalization across all series
    const allValues = [...hourly.hoje, ...hourly.ontem, ...hourly.semana_anterior];
    const maxVal = Math.max(...allValues, 1);

    // Generate points for a line
    const generatePoints = (data: number[]) => {
      return data.map((v, i) => {
        const x = padding + (i / 23) * chartWidth;
        const y = padding + chartHeight - (v / maxVal) * chartHeight;
        return { x, y };
      });
    };

    // Generate path string from points
    const generatePath = (data: number[]) => {
      const points = generatePoints(data);
      return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    };

    // Generate area path (closed shape for fill)
    const generateAreaPath = (data: number[]) => {
      const points = generatePoints(data);
      const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
      const bottomRight = `L ${padding + chartWidth} ${padding + chartHeight}`;
      const bottomLeft = `L ${padding} ${padding + chartHeight}`;
      return `${linePath} ${bottomRight} ${bottomLeft} Z`;
    };

    return {
      areaPath: generateAreaPath(hourly.hoje),
      todayPath: generatePath(hourly.hoje),
      ontemPath: generatePath(hourly.ontem),
      semanaPath: generatePath(hourly.semana_anterior),
      max: maxVal
    };
  }, [hourly, chartWidth, chartHeight, padding]);

  // Don't render if all values are zero
  if (max === 1 && hourly.hoje.every(v => v === 0) && hourly.ontem.every(v => v === 0) && hourly.semana_anterior.every(v => v === 0)) {
    return (
      <svg width={width} height={height}>
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#666" fontSize="8">
          Sem dados
        </text>
      </svg>
    );
  }

  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      {/* Area fill for today (blue transparent) */}
      <path
        d={areaPath}
        fill="rgba(59, 130, 246, 0.25)"
      />

      {/* Last week line (green dashed) - render first so it's behind */}
      <path
        d={semanaPath}
        fill="none"
        stroke="#10B981"
        strokeWidth="1"
        strokeDasharray="2 2"
        strokeOpacity="0.8"
      />

      {/* Yesterday line (purple) */}
      <path
        d={ontemPath}
        fill="none"
        stroke="#8B5CF6"
        strokeWidth="1"
        strokeOpacity="0.9"
      />

      {/* Today line (blue) - render last so it's on top */}
      <path
        d={todayPath}
        fill="none"
        stroke="#3B82F6"
        strokeWidth="1.5"
      />

      {/* Optional legend */}
      {showLegend && (
        <g transform={`translate(${padding}, ${height - 12})`}>
          <line x1="0" y1="5" x2="8" y2="5" stroke="#3B82F6" strokeWidth="1.5" />
          <text x="10" y="7" fontSize="6" fill="#999">Hoje</text>

          <line x1="30" y1="5" x2="38" y2="5" stroke="#8B5CF6" strokeWidth="1" />
          <text x="40" y="7" fontSize="6" fill="#999">Ontem</text>

          <line x1="60" y1="5" x2="68" y2="5" stroke="#10B981" strokeWidth="1" strokeDasharray="2 2" />
          <text x="70" y="7" fontSize="6" fill="#999">Semana</text>
        </g>
      )}
    </svg>
  );
};

export default MiniHourlyChart;
